import React, { useEffect, useRef, useState } from 'react';
import { Button, Grid, TextField, Typography } from '@material-ui/core';
import { Save } from '@material-ui/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { LessonContentData } from 'services/dataService';
import { useStyles } from './styles';

const EMPTY_CODE = Array.from({ length: 6 }, () => '');

const normalizeCode = (value) => String(value || '').replace(/\D/g, '').slice(0, 6);

const getQueryCode = (search) => {
  const params = new URLSearchParams(search);
  return normalizeCode(params.get('code'));
};

export default function PresentialContent({
  finishedCourse,
  content,
  courseId,
  learnId,
  prev,
  next,
  hideAnotation,
}) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [codeDigits, setCodeDigits] = useState(EMPTY_CODE);
  const [validated, setValidated] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (hideAnotation) {
      hideAnotation(false);
    }
  }, [hideAnotation]);

  useEffect(() => {
    setCodeDigits(EMPTY_CODE);
    setValidated(false);
  }, [learnId]);

  useEffect(() => {
    const queryCode = getQueryCode(location.search);
    if (!queryCode) return;

    const digits = queryCode.split('').slice(0, 6);
    setCodeDigits([
      digits[0] || '',
      digits[1] || '',
      digits[2] || '',
      digits[3] || '',
      digits[4] || '',
      digits[5] || '',
    ]);
  }, [location.search]);

  const handleDigitChange = (index, value) => {
    const normalizedValue = normalizeCode(value).slice(0, 1);
    setCodeDigits((currentDigits) => {
      const nextDigits = [...currentDigits];
      nextDigits[index] = normalizedValue;
      return nextDigits;
    });

    if (normalizedValue && index < inputRefs.current.length - 1) {
      const nextInput = inputRefs.current[index + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handleDigitKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !codeDigits[index] && index > 0) {
      const previousInput = inputRefs.current[index - 1];
      if (previousInput) {
        previousInput.focus();
      }
    }
  };

  const handleDigitPaste = (event) => {
    event.preventDefault();
    const pastedCode = normalizeCode(event.clipboardData.getData('text'));
    if (!pastedCode) return;

    const pastedDigits = pastedCode.split('').slice(0, 6);
    setCodeDigits((currentDigits) => {
      const nextDigits = [...currentDigits];
      pastedDigits.forEach((digit, index) => {
        nextDigits[index] = digit;
      });
      return nextDigits;
    });

    const focusIndex = Math.min(pastedDigits.length, 5);
    const focusInput = inputRefs.current[focusIndex];
    if (focusInput) {
      focusInput.focus();
    }
  };

  const validatePresence = async () => {
    try {
      const { data } = await LessonContentData.getLessonContent({
        id_lesson: learnId,
        type: content?.type,
      });

      const currentContent = data?.contents?.[0];
      const serverCode = normalizeCode(currentContent?.lesson_code);
      const typedCode = normalizeCode(codeDigits.join(''));

      if (!serverCode) {
        toast.error('Este conteúdo ainda não possui código liberado.');
        return;
      }

      if (typedCode.length !== 6) {
        toast.error('Digite os 6 números do código.');
        return;
      }

      if (typedCode !== serverCode) {
        toast.error('Código inválido. Confira e tente novamente.');
        return;
      }

      await finishedCourse({
        finished: true,
        video_last_sec: null,
        audio_last_sec: null,
      });

      setValidated(true);
      toast.success('Presença validada com sucesso.');
    } catch (error) {
      toast.error('Não foi possível validar o código.');
    }
  };

  const canContinue = validated && next != null;

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>{content?.name}</Typography>
      <Typography className={classes.subtitle}>{content?.description}</Typography>

      <div className={classes.card}>
        {validated ? (
          <div className={classes.validatedBox}>
            Presença validada. Você já pode seguir para a próxima aula.
          </div>
        ) : (
          <>
            <Typography className={classes.meta}>
              Digite o código de 6 dígitos exibido pelo instrutor para liberar a conclusão
              da aula.
            </Typography>
            {getQueryCode(location.search) && (
              <Typography className={classes.codeHint}>
                Código encontrado no link. Confira e toque em “Validar presença”.
              </Typography>
            )}
            <Grid
              container
              spacing={1}
              className={classes.row}
              onPaste={handleDigitPaste}
            >
              {codeDigits.map((digit, index) => (
                <Grid item key={`digit-${index}`}>
                  <TextField
                    inputRef={(ref) => {
                      inputRefs.current[index] = ref;
                    }}
                    className={classes.digitField}
                    variant="outlined"
                    value={digit}
                    onChange={(event) => handleDigitChange(index, event.target.value)}
                    onKeyDown={(event) => handleDigitKeyDown(index, event)}
                    inputProps={{
                      maxLength: 1,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      style: { textAlign: 'center' },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
            <div className={classes.actions}>
              <Button
                color="primary"
                variant="contained"
                size="large"
                className={classes.actionButton}
                onClick={validatePresence}
              >
                Validar presença
              </Button>
            </div>
          </>
        )}
      </div>

      <div className={classes.buttons}>
        <button
          onClick={() => history.push(`/course/${courseId}/learn/${prev}${location.search || ''}`)}
          disabled={prev == null}
        >
          Anterior
        </button>
        <button
          onClick={() => history.push(`/course/${courseId}/learn/${next}${location.search || ''}`)}
          disabled={!canContinue}
          className={clsx([classes.buttonsActive])}
        >
          Próximo
        </button>
      </div>
    </div>
  );
}
